import { useEffect, useState } from 'react'
import HTMLBaseRenderer from '@/content-viewer/components/base-renderers/HTMLBaseRenderer'
import ContentViewerErrorState from '@/content-viewer/components/common/ContentViewerErrorState'
import ContentViewerLoadingSpinner from '@/content-viewer/components/common/ContentViewerLoadingStates'
import { convertMarkdownToHtml } from '@/content-viewer/util/markdown-to-html'
import { buildAgentV2DocketRecordMarkdown } from './build-agent-v2-docket-record-markdown'
import type { DocketRecord } from './docket-record'

/** Render saved source/support without depending on its owner or fetching fresh court data. */
export function DocketRecordViewer({ record }: { record: DocketRecord | undefined }) {
  const markdown = record ? buildAgentV2DocketRecordMarkdown(record) : null
  const [converted, setConverted] = useState<{ markdown: string; html: string } | null>(null)
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    if (markdown === null) return
    let cancelled = false
    setHasError(false)
    convertMarkdownToHtml(markdown)
      .then((html) => {
        if (!cancelled) setConverted({ markdown, html })
      })
      .catch(() => {
        if (!cancelled) setHasError(true)
      })
    return () => {
      cancelled = true
    }
  }, [markdown])

  if (!record) return <ContentViewerErrorState message="This docket record is no longer available." />
  if (hasError) return <ContentViewerErrorState message="Failed to load the docket record." />
  // Stale HTML from an earlier version of the record never shows while the new one converts.
  if (!converted || converted.markdown !== markdown) return <ContentViewerLoadingSpinner size="small" />

  return <HTMLBaseRenderer htmlContent={converted.html} highlightSentences={record.support.selected_sentences} />
}
